import React, { useState, useEffect } from 'react';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { X, Upload, MapPin, Package } from "lucide-react";
import LocationSelector from './LocationSelector';
import { CommanServices } from '@/services/commanServices';
import { InlineEventCreator, InlineTriggerCreator, InlineTypeCreator } from './InlineCreatePopovers';
import EscalationRuleBuilder from './EscalationRuleBuilder';
import ConditionalFollowupTrigger from './ConditionalFollowupTrigger';
import ScenarioRoleField, { RoleGuidanceBanner, blankRole, buildScenarioRoleOverridePatch, canOverrideScenarioRoles, cleanRoles, markScenarioRolesForOverride, rolesValid, rolesSummary } from './ScenarioRoleField';
import AttachmentPreviewModal from './AttachmentPreviewModal';
import { timelineWriteSucceeded } from './geoFenceActions';

const StatusUpdateModal = ({ incident, editingItem, groupKey = 'status_update', onClose, onSuccess, conditionalContext = null, onAddConditional = null, userRoleMap = {}, scenarioStaffingDefaults = null, onRoleCatalogUpsert = null, isAdmin = false, geoFenceTargeting = null }) => {
  // Requested-stage exercises author injects exactly like the Scenario page (full role-based layout).
  // Role-planning mode: scenario page, exercise at Requested, or a REAL incident
  // reviewing its scenario plan (cards join the plan and fire on activation).
  const isScenario = !!incident?.is_scenario || (!!incident?.is_exercise && String(incident?.status || '').toLowerCase() === 'requested') || (!incident?.is_exercise && incident?.plan_state === 'planned');
  const realPlanReview = !incident?.is_exercise && incident?.plan_state === 'planned';
  const realCreate = !editingItem && !incident?.is_exercise && !incident?.is_scenario;
  const planCardEdit = !!editingItem && editingItem?.plan_state === 'planned' && !incident?.is_exercise;
  const scenarioPulledEdit = canOverrideScenarioRoles(incident, editingItem);
  const isRequested = false;
  const [formData, setFormData] = useState({
    statusType: 'In Progress',
    customStatusType: '',
    statusText: '',
    impactedAreas: [],
    resourcesRequested: '',
    updatedBy: null,
    attachments: [],
    // Related incident fields
    relatedIncident: {
      type_id: '',
      event_id: '',
      trigger_id: '',
      location: '',
      severity:'',
    }
  });

  const [appUsers, setAppUsers] = useState([]);
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [events, setEvents] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [resourceChips, setResourceChips] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [locationNames, setLocationNames] = useState({}); // Map of locationId -> display name
  const [showRelatedIncident, setShowRelatedIncident] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [classificationBundle, setClassificationBundle] = useState(null);
  const [escalation, setEscalation] = useState(null); // Phase 2: conditional inject rule
  const [roles, setRoles] = useState([blankRole()]);
  const [note, setNote] = useState('');
  const [roleOverrideMeta, setRoleOverrideMeta] = useState({});
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const geoFenceMeta = geoFenceTargeting?.dispatchId ? {
    source: 'geo_fence',
    geoFenceTargeting,
    recipientIds: geoFenceTargeting.userIds || [],
  } : {};

  // Prefill role(s) + note from the source card when authoring a conditional follow-up.
  useEffect(() => {
    if (!editingItem && conditionalContext?.sourceRoles?.length) {
      setRoles(conditionalContext.sourceRoles.map((r) => ({ ...r, sla: r.sla == null ? '' : r.sla })));
      if (conditionalContext.sourceNote) setNote(conditionalContext.sourceNote);
    }
  }, [conditionalContext, editingItem]);


  const adminStatusTypes = [
    'In Progress',
    'Contained',
    'Escalating',
    'Awaiting Resources',
    'Resolved',
    'Report Related Incident',
    'Request Input',
    'Replay to Input Request',
    'Custom'
  ];
  const leaderStatusTypes = [
    'In Progress',
    'Contained',
    'Escalating',
    'Awaiting Resources',
    'Custom'
  ];
  const statusTypes = isAdmin ? adminStatusTypes : leaderStatusTypes;
  
  // Fetch location names for IDs
  const fetchLocationNames = async (locationIds) => {
    if (!locationIds || locationIds.length === 0) return;
    
    try {
      console.log('🔍 Fetching location names for:', locationIds);
      const response = await CommanServices.getlocationszonesdata();
      
      if (response.status) {
        const allLocations = await response.data;
        
        const names = {};
        
        // Build a flat map of all location IDs to their display paths
        const buildLocationMap = (locs, parentPath = '') => {
          if (!locs || !Array.isArray(locs)) {
            console.warn('⚠️ locs is not an array:', locs);
            return;
          }
          
          locs.forEach(loc => {
            if (!loc || !loc.id) {
              console.warn('⚠️ Invalid location object:', loc);
              return;
            }
            
            const displayName = loc.name || loc.zone_name || loc.label || 'Unknown';
            const currentPath = parentPath ? `${parentPath} > ${displayName}` : displayName;
            names[loc.id] = currentPath;
            console.log(`✅ Mapped ${loc.id} -> ${currentPath}`);
            
            if (loc.children && loc.children.length > 0) {
              buildLocationMap(loc.children, currentPath);
            }
            if (loc.subzones && loc.subzones.length > 0) {
              buildLocationMap(loc.subzones, currentPath);
            }
          });
        };
        
        buildLocationMap(allLocations);
        console.log('🗺️ Final location names map:', names);
        setLocationNames(names);
      } else {
        console.error('❌ Failed to fetch locations:', response.status);
      }
    } catch (error) {
      console.error('❌ Error fetching location names:', error);
    }
  };
  
  // Fetch location names when impactedAreas changes
  useEffect(() => {
    if (formData.impactedAreas && formData.impactedAreas.length > 0) {
      fetchLocationNames(formData.impactedAreas);
    }
  }, [formData.impactedAreas]);

  // Fetch app users
  useEffect(() => {
    const fetchAppUsers = async () => {
      try {
        const response = await CommanServices.getAppUsers();
        if (response.status) {
          const users = await response.data;
          setAppUsers(users);
        }
      } catch (error) {
        console.error('Error fetching app users:', error);
      }
    };

    const fetchDepartments = async () => {
      try {
        const response = await CommanServices.departments();
        if (response.status) {
          const data = await response.data;
          console.log('Departments loaded:', data);
          setDepartments(data);
        }
      } catch (error) {
        console.error('Error fetching departments:', error);
      }
    };

    if (isAdmin) {
      fetchAppUsers();
      fetchDepartments();
    }
  }, [isAdmin]);

  // Fetch incident types for related incident
  useEffect(() => {
    if (isAdmin && showRelatedIncident) {
      fetchIncidentTypes();
      setClassificationBundle(null);
    }
  }, [isAdmin, showRelatedIncident]);

  // Fetch events and triggers when type changes
  useEffect(() => {
    if (formData.relatedIncident.type_id) {
      fetchEvents(formData.relatedIncident.type_id);
    } else {
      setEvents([]);
      setTriggers([]);
      setClassificationBundle(null);
    }
  }, [formData.relatedIncident.type_id]);

  const fetchIncidentTypes = async (type) => {
    try {
      const response = await CommanServices.incidentClassifications()
      if (response.status) {
        const data = await response.data;
        console.log('✅ Fetched incident types:', data.length, 'types');
        setIncidentTypes(data);
        if (type && type.id) {
          setFormData(prev => ({
            ...prev,
            relatedIncident: { ...prev.relatedIncident, type_id: type.id, event_id: '', trigger_id: '' }
          }))
        }
      }
    } catch (error) {
      console.error('❌ Error fetching incident types:', error);
      setIncidentTypes([]);
    }
  };

  const fetchEvents = async (typeId) => {
    try {
      const response = await CommanServices.getincidentclassificationtypes(typeId)
      
      if (response.status) {
        const bundle = await response.data;
        console.log('✅ Fetched classification bundle:', bundle);
        setEvents(bundle.events || []);
        setTriggers(bundle.triggers || []);
        setClassificationBundle(bundle);
      } else {
        console.error('❌ Failed to fetch classification bundle:', response.status);
        setEvents([]);
        setTriggers([]);
        setClassificationBundle(bundle);
      }
    } catch (error) {
      console.error('❌ Error fetching events:', error);
      setEvents([]);
      setTriggers([]);
      setClassificationBundle(bundle);
    }
  };

  // Initialize form from editing item
  useEffect(() => {
    if (editingItem) {
      setIsEditMode(true);
      const meta = editingItem.meta || {};
      setRoleOverrideMeta(meta);
      setEscalation(meta.escalation || null);
      setRoles(Array.isArray(meta.scenarioRoles) && meta.scenarioRoles.length
        ? markScenarioRolesForOverride(meta.scenarioRoles, scenarioPulledEdit)
        : [blankRole()]);
      setNote(meta.assignmentNote || '');
      
      // Ensure impactedAreas is always an array
      let impactedAreasArray = [];
      if (Array.isArray(meta.impactedAreas)) {
        impactedAreasArray = meta.impactedAreas;
      } else if (typeof meta.impactedAreas === 'string' && meta.impactedAreas) {
        impactedAreasArray = [meta.impactedAreas];
      }
      
      setFormData({
        statusType: meta.statusType || 'In Progress',
        customStatusType: meta.customStatusType || '',
        statusText: meta.statusText || editingItem.details || '',
        impactedAreas: impactedAreasArray,
        resourcesRequested: '',
        updatedBy: editingItem.actor || null,
        attachments: editingItem.attachments || [],
        relatedIncident: meta.relatedIncident || {
          type_id: '',
          event_id: '',
          trigger_id: '',
          location: ''        }
      });
      
      // Show related incident section if data exists
      if (meta.relatedIncident && meta.relatedIncident.type_id) {
        setShowRelatedIncident(true);
      }
      
      setResourceChips(meta.resourcesRequested || []);
    } else {
      // Get current user from local storage
      const userStr = localStorage.getItem('user') || localStorage.getItem('userData');
      let currentActor = {
        id: 'current_user',
        name: 'Reporter',
        type: 'user'
      };

      if (userStr) {
        try {
          const user = JSON.parse(userStr);
          currentActor = {
            id: user.id || 'current_user',
            name: user.full_name || user.name || 'Reporter',
            type: 'user',
            email: user.email || ''
          };
        } catch (e) {
          console.error("Error parsing user data for StatusUpdateModal:", e);
        }
      }

      // Auto-fill current user
      setFormData(prev => ({
        ...prev,
        updatedBy: currentActor
      }));
    }
  }, [editingItem]);

  // Handle adding resource chip
  const handleAddResource = () => {
    if (formData.resourcesRequested.trim()) {
      setResourceChips([...resourceChips, formData.resourcesRequested.trim()]);
      setFormData(prev => ({ ...prev, resourcesRequested: '' }));
    }
  };

  // Handle removing resource chip
  const handleRemoveResource = (index) => {
    setResourceChips(resourceChips.filter((_, i) => i !== index));
  };

  // Handle file upload — persist to Emergent Object Storage so media survives reloads (AAR/evaluator review)
  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);
    setError('');

    try {
      const uploadData = new FormData();
      files.forEach((file) => uploadData.append('files', file));

      const response = await CommanServices.uploadGenericFile(uploadData, 'status_update');

      if (!response?.status) {
        throw new Error(response?.message || 'Upload failed');
      }

      // Backend returns a single object for one file, or an array for many
      const raw = Array.isArray(response.data) ? response.data : [response.data];
      const uploadedFiles = raw.map((m, i) => ({
        filename: m.filename || m.name || files[i]?.name || 'file',
        url: m.url || m.src || m.preview_url,
        key: m.key,
        type: m.type || m.content_type || files[i]?.type,
        size: m.size || files[i]?.size,
      }));

      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedFiles]
      }));

      console.log('📎 Files uploaded to storage:', uploadedFiles);
    } catch (error) {
      console.error('Error uploading files:', error);
      setError(error.message || 'Failed to upload files. Please try again.');
    } finally {
      setUploadingFiles(false);
      // Reset input so the same file can be re-selected if removed
      if (e?.target) e.target.value = '';
    }
  };

  // Handle removing attachment
  const handleRemoveAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (isAdmin && isScenario && (planCardEdit || !realPlanReview)) {
      if (!rolesValid(roles, incident?.is_scenario ? scenarioStaffingDefaults : null)) {
        setError('Add at least one role.');
        return;
      }
      setLoading(true);
      setError('');
      try {
        const overridePatch = scenarioPulledEdit
          ? buildScenarioRoleOverridePatch(roleOverrideMeta, roles)
          : null;
        if (overridePatch?.error) {
          setError(overridePatch.error);
          setLoading(false);
          return;
        }
        const clean = overridePatch?.scenarioRoles || cleanRoles(roles);
        const finalStatusType = formData.statusType === 'Custom' ? (formData.customStatusType || 'Custom') : formData.statusType;
        const meta = {
          statusType: formData.statusType,
          customStatusType: formData.customStatusType,
          statusText: formData.statusText,
          scenarioRoles: clean,
          ...(overridePatch ? {
            roleAssignments: overridePatch.roleAssignments,
            history: overridePatch.history,
            roleOverrideHistory: overridePatch.roleOverrideHistory,
          } : {}),
          assignmentNote: note || '',
          escalation: escalation || null,
          ...geoFenceMeta,
          ...(!overridePatch ? { history: Array.isArray(editingItem?.meta?.history) ? editingItem.meta.history : [] } : {}),
          ...(conditionalContext?.conditional ? { conditional: conditionalContext.conditional } : {}),
        };
        const base = {
          incident_id: incident.id,
          group: groupKey,
          timestamp: new Date().toISOString(),
          actor: { id: 'role', name: rolesSummary(clean), type: 'role' },
          title: `Status Update: ${finalStatusType}`,
          details: formData.statusText || rolesSummary(clean),
          chips: [finalStatusType],
          attachments: formData.attachments,
          meta,
        };
        let response;
        if (isEditMode && editingItem) {
          response = await CommanServices.updatePutIncidentTimeline(incident.id, editingItem.id, { ...editingItem, ...base, attachments: formData.attachments, meta: { ...(editingItem.meta || {}), ...meta } });
        } else {
          response = await CommanServices.addincidentTimeline(incident.id, base);
        }
        if (!timelineWriteSucceeded(response)) throw new Error(response?.message || 'Failed to save status update');
        onSuccess && onSuccess();
        onClose();
      } catch (error) {
        console.error('Scenario role status update error:', error);
        setError(error.message || 'Failed to save roles');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Validation
    if (!formData.statusText.trim()) {
      setError('Please enter status/progress text');
      return;
    }

    if (formData.statusType === 'Custom' && !formData.customStatusType.trim()) {
      setError('Please enter custom status type');
      return;
    }

    if (!formData.updatedBy) {
      setError('Please select who is updating');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create related incident if data is provided
      let relatedIncidentId = null;
      console.log('🔍 Checking related incident:', {
        showRelatedIncident,
        hasTypeId: !!formData.relatedIncident.type_id,
        isEditMode,
        formData: formData.relatedIncident
      });
      
      if (isAdmin && showRelatedIncident && formData.relatedIncident.type_id && (!isEditMode || !editingItem?.meta?.relatedIncidentId)) {
        try {
          console.log('🚀 Creating related incident...');

          const relatedReporterGps = await new Promise((resolve) => {
            if (!("geolocation" in navigator)) {
              resolve(null);
              return;
            }
            navigator.geolocation.getCurrentPosition(
              (position) => resolve({
                lat: position.coords.latitude,
                lng: position.coords.longitude,
                accuracy: position.coords.accuracy,
                captured_at: new Date().toISOString(),
              }),
              () => resolve(null),
              { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
            );
          });
          if (!relatedReporterGps) {
            throw new Error('Reporter GPS is required to create the related incident. Allow location access and try again.');
          }
          
          // Build as FormData so the backend's multipart form parser can read it
          const incidentPayload = new FormData();
          incidentPayload.append('type_id', formData.relatedIncident.type_id || '');
          incidentPayload.append('event_id', formData.relatedIncident.event_id && formData.relatedIncident.event_id !== 'none' ? formData.relatedIncident.event_id : '');
          incidentPayload.append('trigger_id', formData.relatedIncident.trigger_id && formData.relatedIncident.trigger_id !== 'none' ? formData.relatedIncident.trigger_id : '');
          if (formData.relatedIncident.location) {
            incidentPayload.append('location[0]', formData.relatedIncident.location);
          }
          incidentPayload.append('severity', formData.relatedIncident.severity || '');
          incidentPayload.append('description', `Related incident reported from status update of incident ${incident.id}`);
          incidentPayload.append('reporter_name', formData.updatedBy?.name || 'Status Update Reporter');
          incidentPayload.append('reporter_contact', formData.updatedBy?.email || formData.updatedBy?.phone || '');
          incidentPayload.append('title', `Related to ${incident.id}`);
          incidentPayload.append('notify_now', 'false');
          incidentPayload.append('parent_incident_id', incident.id);
          incidentPayload.append('gps_data', JSON.stringify(relatedReporterGps));

          console.log('📤 Incident payload (FormData)', {
            type_id: formData.relatedIncident.type_id,
            event_id: formData.relatedIncident.event_id,
            trigger_id: formData.relatedIncident.trigger_id,
            location: formData.relatedIncident.location,
            parent_incident_id: incident.id,
          });

          const incidentResponse = await CommanServices.addincidents(incidentPayload)

          console.log('📥 Response status:', incidentResponse.status);

          if (incidentResponse.status) {
            const createdIncident = await incidentResponse.data;
            relatedIncidentId = createdIncident.id;
            console.log('✅ Created related incident:', relatedIncidentId);
          } else {
            const errorText = await incidentResponse.message;
            console.error('❌ Failed to create related incident:', incidentResponse.status, errorText);
          }
        } catch (error) {
          console.error('❌ Error creating related incident:', error);
          setError(error?.message || 'Could not create the related incident.');
        }
      } else {
        console.log('⏭️ Skipping incident creation');
      }

      const finalStatusType = formData.statusType === 'Custom' 
        ? formData.customStatusType 
        : formData.statusType;

      const updatePayload = isEditMode ? {
        ...editingItem,
        timestamp: editingItem.timestamp,
        title: `Status Update: ${finalStatusType}`,
        details: formData.statusText,
        actor: formData.updatedBy,
        attachments: formData.attachments,
        chips: [finalStatusType],
        meta: {
          ...(editingItem.meta || {}),
          statusType: formData.statusType,
          customStatusType: formData.customStatusType,
          statusText: formData.statusText,
          impactedAreas: formData.impactedAreas,
          resourcesRequested: resourceChips,
          requestInputDepartment: isAdmin && formData.statusType === 'Request Input' && selectedDepartment
            ? {
                department_id: selectedDepartment,
                department_name: departments.find(d => d.id === selectedDepartment)?.department_name || 'Unknown'
              }
            : editingItem.meta?.requestInputDepartment,
          relatedIncident: isAdmin && showRelatedIncident && formData.relatedIncident.type_id
            ? {
                ...formData.relatedIncident,
                type_name: incidentTypes.find(t => t.id === formData.relatedIncident.type_id)?.incident_type,
                event_name: events.find(e => e.id === formData.relatedIncident.event_id)?.name,
                trigger_name: triggers.find(t => t.id === formData.relatedIncident.trigger_id)?.name
              }
            : editingItem.meta?.relatedIncident,
          relatedIncidentId: relatedIncidentId || editingItem.meta?.relatedIncidentId,
          escalation: escalation || null,
          ...geoFenceMeta,
          history: [
            ...(editingItem.meta?.history || []),
            {
              timestamp: new Date().toISOString(),
              actor: formData.updatedBy.name,
              action: 'Updated Status',
              changes: {
                statusType: finalStatusType,
                statusText: formData.statusText
              }
            }
          ]
        }
      } : {
        incident_id: incident.id,
        group: groupKey, // Use the passed groupKey instead of hardcoded 'status_update'
        timestamp: new Date().toISOString(),
        actor: formData.updatedBy,
        title: `Status Update: ${finalStatusType}`,
        details: formData.statusText,
        chips: [finalStatusType],
        attachments: formData.attachments,
        meta: {
          statusType: formData.statusType,
          customStatusType: formData.customStatusType,
          statusText: formData.statusText,
          impactedAreas: formData.impactedAreas,
          resourcesRequested: resourceChips,
          requestInputDepartment: isAdmin && formData.statusType === 'Request Input' && selectedDepartment
            ? {
                department_id: selectedDepartment,
                department_name: departments.find(d => d.id === selectedDepartment)?.department_name || 'Unknown'
              }
            : null,
          relatedIncident: isAdmin && showRelatedIncident && formData.relatedIncident.type_id
            ? {
                ...formData.relatedIncident,
                type_name: incidentTypes.find(t => t.id === formData.relatedIncident.type_id)?.incident_type,
                event_name: events.find(e => e.id === formData.relatedIncident.event_id)?.name,
                trigger_name: triggers.find(t => t.id === formData.relatedIncident.trigger_id)?.name,
                incident_id: relatedIncidentId
              }
            : null,
          escalation: escalation || null,
          ...geoFenceMeta,
          history: []
        }
      };

      
      if (isEditMode) {
        const res = await CommanServices.updatePutIncidentTimeline(incident.id, editingItem.id, updatePayload)
        if (!timelineWriteSucceeded(res)) {
          throw new Error('Failed to save status update');
        }
      }else{
        const response = await CommanServices.addincidentTimeline(incident.id, updatePayload)
        if (!timelineWriteSucceeded(response)) {
          throw new Error('Failed to save status added');
        }
      }

      console.log('✅ Status update saved successfully');
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving status update:', error);
      setError(error.message || 'Failed to save status update');
    } finally {
      setLoading(false);
    }
  };

  // Severity calculation using exact stored values from classification bundle
  const calculateAutoSeverity = () => {
    if (!classificationBundle) return { severity: '', from: '' };
    
    const typeSeverity = classificationBundle.type.severity;

    // 1. Check trigger severity (highest priority)
    if (formData.relatedIncident.trigger_id && formData.relatedIncident.trigger_id !== "none") {
      const selectedTrigger = triggers.find(t => t.id === formData.relatedIncident.trigger_id && t.event_id === formData.relatedIncident.event_id);
      if (selectedTrigger?.severity) {
        return { severity: selectedTrigger.severity, from: 'Trigger' };
      }
    }
    
    // 2. Check event severity (medium priority) 
    if (formData.relatedIncident.event_id && formData.relatedIncident.event_id !== "none") {
      const selectedEvent = events.find(e => e.id === formData.relatedIncident.event_id);
      if (selectedEvent?.severity) {
        return { severity: selectedEvent.severity, from: 'Event' };
      }
    }

    // 3. Fall back to type severity (lowest priority)
    return { severity: typeSeverity, from: 'Type' };
  };

  useEffect(() => {
      const severityData = calculateAutoSeverity();
      setFormData(prev => ({
        ...prev,
        relatedIncident: { ...prev.relatedIncident, severity: severityData?.severity?.toLowerCase() }
      }))
    }, [formData.relatedIncident.type_id, formData.relatedIncident.event_id, formData.relatedIncident.trigger_id, events, triggers, classificationBundle]);

  console.log("formData.relatedIncident", formData.relatedIncident)
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {isEditMode ? 'Edit Status Update' : 'Add Status Update'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {/* Updated By */}
          {isAdmin && isScenario && !realCreate && (
            <ScenarioRoleField roles={roles} setRoles={setRoles} note={note} setNote={setNote} showSla={false} roleCatalog={incident?.is_scenario ? scenarioStaffingDefaults : null} onRoleCatalogUpsert={onRoleCatalogUpsert} lockScenarioDefaultRoles={scenarioPulledEdit} roleAssignments={roleOverrideMeta?.roleAssignments || {}} />
          )}
          {isRequested && (
            <RoleGuidanceBanner roles={editingItem?.meta?.scenarioRoles} note={editingItem?.meta?.assignmentNote} />
          )}
          <div className={isScenario && !realPlanReview ? 'opacity-50 pointer-events-none select-none' : ''}>
            {isScenario && !realPlanReview && <p className="text-[11px] text-violet-600 font-medium mb-1">Assignees are configured through Required Roles and Suggested staffing while planning.</p>}
            <Label htmlFor="updatedBy">Updated By *</Label>
            {!isScenario && (
              <p className="text-[11px] text-gray-500 mb-1">Choose a drill participant. Each user keeps the role they were assigned during planning.</p>
            )}
            {isAdmin ? (
              <Select
                value={formData.updatedBy?.id || ''}
                onValueChange={(selectedUserId) => {
                  const selected = appUsers.find(u => u.id === selectedUserId);
                  if (selected) {
                    setFormData(prev => ({
                      ...prev,
                      updatedBy: {
                        id: selected.id,
                        name: selected.name || selected.email,
                        type: 'user'
                      }
                    }));
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select user..." />
                </SelectTrigger>
                <SelectContent>
                  {appUsers.map(user => {
                    const role = userRoleMap[String(user.id)] || userRoleMap[String(user.name)];
                    return (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name || user.email}{role ? ` — ${role}` : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            ) : (
              <div className="mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700">
                {formData.updatedBy?.name || 'Current user'}
              </div>
            )}
          </div>

          {/* Status Type */}
          <div>
            <Label htmlFor="statusType">Status Type *</Label>
            <Select
              value={formData.statusType}
              onValueChange={(value) => setFormData(prev => ({ ...prev, statusType: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status type..." />
              </SelectTrigger>
              <SelectContent>
                {statusTypes.map(type => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Status Type (if Custom selected) */}
          {formData.statusType === 'Custom' && (
            <div>
              <Label htmlFor="customStatusType">Custom Status Type *</Label>
              <Input
                id="customStatusType"
                value={formData.customStatusType}
                onChange={(e) => setFormData(prev => ({ ...prev, customStatusType: e.target.value }))}
                placeholder="Enter custom status type..."
              />
            </div>
          )}

          {/* Department Dropdown (if Request Input selected) */}
          {isAdmin && formData.statusType === 'Request Input' && (
            <div>
              <Label htmlFor="department">Department *</Label>
              <Select
                value={selectedDepartment}
                onValueChange={(value) => setSelectedDepartment(value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select department..." />
                </SelectTrigger>
                <SelectContent>
                  {departments.map(dept => (
                    <SelectItem key={dept.id} value={dept.id}>
                      {dept.department_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Status/Progress Text */}
          <div>
            <Label htmlFor="statusText">Status/Progress Description *</Label>
            <textarea
              id="statusText"
              value={formData.statusText}
              onChange={(e) => setFormData(prev => ({ ...prev, statusText: e.target.value }))}
              placeholder="Describe the current status or progress..."
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              rows={4}
            />
          </div>

          {/* Impacted Areas */}
          <div>
            <Label>
              <MapPin className="inline h-4 w-4 mr-1" />
              Impacted Areas
            </Label>
            <div className="mt-2">
              <LocationSelector
                value={formData.impactedAreas}
                onChange={(locations) => {
                  console.log('📍 LocationSelector onChange:', locations);
                  // LocationSelector returns array of location IDs in multi mode
                  // We need to store them as is for now
                  const locArray = Array.isArray(locations) ? locations : (locations ? [locations] : []);
                  setFormData(prev => ({ ...prev, impactedAreas: locArray }));
                }}
                mode="multiple"
                placeholder="Select affected locations..."
              />
              
              {/* Display selected locations as chips */}
              {formData.impactedAreas && Array.isArray(formData.impactedAreas) && formData.impactedAreas.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {formData.impactedAreas.map((locationId, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                    >
                      <MapPin className="h-3 w-3" />
                      {locationNames[locationId] || locationId}
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            impactedAreas: prev.impactedAreas.filter((_, i) => i !== idx)
                          }));
                        }}
                        className="hover:text-blue-900"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Resources Requested */}
          <div>
            <Label>
              <Package className="inline h-4 w-4 mr-1" />
              Resources Requested
            </Label>
            <div className="flex gap-2 mt-2">
              <Input
                value={formData.resourcesRequested}
                onChange={(e) => setFormData(prev => ({ ...prev, resourcesRequested: e.target.value }))}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddResource();
                  }
                }}
                placeholder="e.g., Medical supplies, Personnel, Equipment..."
              />
              <Button
                type="button"
                onClick={handleAddResource}
                variant="outline"
                size="sm"
              >
                Add
              </Button>
            </div>
            
            {/* Resource Chips */}
            {resourceChips.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {resourceChips.map((resource, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    {resource}
                    <button
                      type="button"
                      onClick={() => handleRemoveResource(index)}
                      className="hover:text-blue-900"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Related Incident Section */}
          {isAdmin && <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-base font-semibold">Report Related Incident (Optional)</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowRelatedIncident(!showRelatedIncident)}
              >
                {showRelatedIncident ? 'Hide' : 'Show'}
              </Button>
            </div>
            
            {showRelatedIncident && (
              <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                {/* Type */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Type *</Label>
                    <InlineTypeCreator 
                      onTypeCreated={fetchIncidentTypes}
                    />
                  </div>
                  <Select
                    value={formData.relatedIncident.type_id}
                    onValueChange={(value) => {
                      if (value) {
                        setFormData(prev => ({
                          ...prev,
                          relatedIncident: { ...prev.relatedIncident, type_id: value, event_id: '', trigger_id: '' }
                        }))
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select incident type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {incidentTypes.length === 0 ? (
                        <SelectItem value="no-types" disabled>
                          Loading types...
                        </SelectItem>
                      ) : (
                        incidentTypes.map(type => (
                          <SelectItem key={type.id} value={type.id}>
                            {type.incident_type}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Event */}
                {formData.relatedIncident.type_id && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label>Event *</Label>
                      <InlineEventCreator 
                        selectedTypeId={formData.relatedIncident.type_id}
                        onEventCreated={(data) => {
                          fetchEvents(formData.relatedIncident.type_id);
                          if (data && data.event) {
                            setFormData(prev => ({
                              ...prev,
                              relatedIncident: { ...prev.relatedIncident, event_id: data.event, trigger_id: '' }
                            }))
                          }
                        }}
                        disabled={!formData.relatedIncident.type_id}
                      />
                    </div>
                    <Select
                      value={formData.relatedIncident.event_id}
                      onValueChange={(value) => {
                        if (value) {
                          setFormData(prev => ({
                            ...prev,
                            relatedIncident: { ...prev.relatedIncident, event_id: value, trigger_id: '' }
                          }))
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select event..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No specific event</SelectItem>
                        {events.length === 0 ? (
                          <SelectItem value="no-events" disabled>
                            No events defined for this type
                          </SelectItem>
                        ) : (
                          events.map(event => (
                            <SelectItem key={event.id} value={event.id}>
                              {event.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Trigger */}
                {formData.relatedIncident.event_id && (
                  <div>
                     <div className="flex items-center justify-between mb-2">
                      <Label>Trigger *</Label>
                      <InlineTriggerCreator 
                        selectedTypeId={formData.relatedIncident.type_id}
                        selectedEventName={formData.relatedIncident.event_id && formData.relatedIncident.event_id !== "none" ? 
                          events.find(e => e.id === formData.relatedIncident.event_id)?.name : null
                        }
                        onTriggerCreated={(data) => {
                          fetchEvents(formData.relatedIncident.type_id);
                          if (data) {
                            setFormData(prev => ({
                              ...prev,
                              relatedIncident: { ...prev.relatedIncident, trigger_id: data }
                            }))
                          }
                        }}
                        disabled={!formData.relatedIncident.event_id || formData.relatedIncident.event_id === "none"}
                      />
                    </div>
                    <Select
                      value={formData.relatedIncident.trigger_id}
                      onValueChange={(value) => {
                        if (value) {
                          setFormData(prev => ({
                            ...prev,
                            relatedIncident: { ...prev.relatedIncident, trigger_id: value }
                          }))
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select trigger..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No specific trigger</SelectItem>
                        {(() => {
                          // Filter triggers by selected event
                          let filteredTriggers = [];
                          
                          if (formData.relatedIncident.event_id === "none") {
                            filteredTriggers = triggers;
                          } else if (formData.relatedIncident.event_id) {
                            filteredTriggers = triggers.filter(trigger => 
                              trigger.event_id === formData.relatedIncident.event_id
                            );
                          }
                          
                          if (filteredTriggers.length === 0) {
                            return (
                              <SelectItem value="no-triggers" disabled>
                                No triggers defined for this event
                              </SelectItem>
                            );
                          }
                          
                          return filteredTriggers.map(trigger => (
                            <SelectItem key={trigger.id} value={trigger.id}>
                              {trigger.name}
                            </SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Location */}
                <div>
                  <Label>Location *</Label>
                  <LocationSelector
                    value={formData.relatedIncident.location}
                    onChange={async (locationId) => {
                      // Fetch location name
                      let locationName = locationId;
                      try {
                        const response = await CommanServices.getlocationszonesdata()
                        if (response.status) {
                          const allLocations = await response.data;
                          
                          // Recursively search for location
                          const findLocationName = (locs, targetId) => {
                            for (const loc of locs) {
                              if (loc.id === targetId) {
                                return loc.name || loc.zone_name || loc.label || targetId;
                              }
                              if (loc.children) {
                                const found = findLocationName(loc.children, targetId);
                                if (found) return found;
                              }
                              if (loc.subzones) {
                                const found = findLocationName(loc.subzones, targetId);
                                if (found) return found;
                              }
                            }
                            return null;
                          };
                          
                          const name = findLocationName(allLocations, locationId);
                          if (name) locationName = name;
                        }
                      } catch (error) {
                        console.error('Error fetching location name:', error);
                      }
                      
                      setFormData(prev => ({
                        ...prev,
                        relatedIncident: { 
                          ...prev.relatedIncident, 
                          location: locationId,
                          location_name: locationName
                        }
                      }));
                    }}
                    placeholder="Select location..."
                  />
                </div>
              </div>
            )}
          </div>}

          {/* Attachments */}
          <div>
            <Label>
              <Upload className="inline h-4 w-4 mr-1" />
              Attachments
            </Label>
            <div className="mt-2">
              <input
                type="file"
                multiple
                onChange={handleFileUpload}
                data-testid="status-attachment-input"
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                disabled={uploadingFiles}
              />
              {uploadingFiles && (
                <div className="mt-2 text-sm text-gray-500">Uploading files...</div>
              )}
            </div>

            {/* Attachment List */}
            {formData.attachments.length > 0 && (
              <div className="mt-3 space-y-2">
                {formData.attachments.map((attachment, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-gray-50 border border-gray-200 rounded"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <Upload className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      {attachment.url ? (
                        <button
                          type="button"
                          onClick={() => setPreviewAttachment({ ...attachment, name: attachment.filename, src: attachment.url })}
                          className="text-sm text-blue-600 hover:underline truncate text-left"
                          data-testid={`status-attachment-link-${index}`}
                          title="Click to preview"
                        >
                          {attachment.filename}
                        </button>
                      ) : (
                        <span className="text-sm text-gray-700 truncate">
                          {attachment.filename}
                        </span>
                      )}
                      {attachment.size != null && (
                        <span className="text-xs text-gray-500">
                          ({Math.round(attachment.size / 1024)}KB)
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachment(index)}
                      className="text-red-500 hover:text-red-700 ml-2"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Conditional follow-ups: not applicable to Status Update cards */}
          <div className="mt-4">
            {!isScenario && (
              <EscalationRuleBuilder value={escalation} onChange={setEscalation} />
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Status Update'}
          </Button>
        </div>
      </div>
      {previewAttachment && (
        <AttachmentPreviewModal attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} />
      )}
    </div>
  );
};

export default StatusUpdateModal;
